import { Component, OnInit, inject } from '@angular/core';
import { FormGroup, Validators, ReactiveFormsModule, NonNullableFormBuilder } from '@angular/forms';
import { RecaptchaService } from '@app/core/services/recaptcha.service';
import { AccountService } from '@api';
import { switchMap, shareReplay, map, catchError, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { WaitIndicatorService } from '@app/core/services/wait-indicator.service';
import { FormsService, FormValidator } from '@app/core/services/forms.service';
import { AlertService } from '@app/core/services/alert.service';
import { ControlsOf } from '@app/core/functions/controls-of';
import { WtFocusableDirective } from '@app/core/directives/wt-focusable.directive';
import { WtAutofocusDirective } from '@app/core/directives/wt-autofocus.directive';
import { WtValidationStateDirective } from '@app/core/directives/wt-validation-state.directive';
import { AsyncPipe } from '@angular/common';

interface PageState {
  submitted: boolean;
}

@Component({
  selector: 'app-request-password-reset',
  template: `<h2 class="title">Having trouble signing in?</h2>

    @if (pageState$ | async; as pageState) {
      @if (pageState.submitted) {
        <div>
          <div class="alert alert-success" role="alert">
            <span class="glyphicon glyphicon-ok-sign" aria-hidden="true"></span>
            <span class="sr-only">Success:</span>
            Please check your email for a link to reset your password.
          </div>
        </div>
      }
      @if (!pageState.submitted) {
        <div>
          <p>Enter your MyLoanInfo username to get started.</p>
          <form [formGroup]="form" class="form-horizontal col-sm-8" (ngSubmit)="onSubmit()" novalidate>
            <div class="row form-group required" [wtValidationState]="validator.getControlStatus('username') | async">
              <label class="col-md-3 control-label" for="id_username"> Username </label>
              <div class="col-md-9">
                <input
                  formControlName="username"
                  class="form-control"
                  id="id_username"
                  maxlength="50"
                  name="username"
                  placeholder="Username"
                  required="required"
                  type="text"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck="false"
                  autocomplete="off"
                  wtAutofocus
                  wtFocusable
                />
              </div>
            </div>
            <p>If you forgot your MyLoanInfo username, you can <a href="/loanmanager/recover_username">look it up</a>.</p>
            <div class="row form-group">
              <div class="col-md-offset-3 col-md-9">
                <button class="btn btn-primary" [disabled]="!form.valid" type="submit">Continue</button>
              </div>
            </div>
          </form>
        </div>
      }
    }`,
  imports: [ReactiveFormsModule, WtValidationStateDirective, WtAutofocusDirective, WtFocusableDirective, AsyncPipe],
})
export class RequestPasswordResetComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private recaptchaService = inject(RecaptchaService);
  private accountService = inject(AccountService);
  private waitIndicatorService = inject(WaitIndicatorService);
  private formsService = inject(FormsService);
  private alertService = inject(AlertService);

  public form: FormGroup<
    ControlsOf<{
      username: string;
    }>
  >;
  public validator: FormValidator;
  public pageState$: Observable<PageState> = of({ submitted: false });

  ngOnInit() {
    this.form = this.fb.group({
      username: this.fb.control('', Validators.required),
    });
    this.validator = this.formsService.getFormValidator(this.form);
  }

  onSubmit() {
    this.validator.setSubmitted();

    if (!this.form.valid) {
      return of(null);
    }

    this.waitIndicatorService.setWaiting();

    this.pageState$ = this.recaptchaService.getNewRecaptchaToken('request_password_reset').pipe(
      switchMap((recaptcha_response) =>
        this.accountService
          .accountRequestPasswordResetCreate({
            requestPasswordReset: {
              username: this.form.getRawValue().username,
              recaptcha_response: recaptcha_response,
            },
          })
          .pipe(
            map(() => {
              this.waitIndicatorService.setReady();
              return { submitted: true };
            }),
            catchError(() => {
              this.alertService.alert('There was a problem requesting a password reset');
              return of({ submitted: false });
            })
          )
      ),
      tap(() => {
        this.waitIndicatorService.setReady();
      }),
      shareReplay()
    );

    return this.pageState$;
  }
}
